import { useEffect, useState } from "react";
import { api, formatEUR, formatDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardShell } from "@/components/Shell";
import { Wallet, TrendingUp, Calendar, ListChecks } from "lucide-react";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [recent, setRecent] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/staff/me/wallet").then((r) => setWallet(r.data));
    api.get("/staff/me/tips").then((r) => setRecent(r.data));
    if (user?.id) api.get(`/staff/${user.id}/locations`).then((r) => setLocations(r.data));
  }, [user]);

  return (
    <DashboardShell role="staff">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Hallo {user?.name?.split(" ")[0]}</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2">Dein Wallet</h1>
        </div>
        <div className="text-xs text-[#A1A1AA]">Live-Daten · {new Date().toLocaleDateString("de-DE")}</div>
      </div>

      {/* Big total card */}
      <div className="relative bg-gradient-to-br from-[#1A1A20] to-[#0d0d11] border border-white/10 rounded-3xl p-8 overflow-hidden mb-6" data-testid="staff-wallet-card">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#EBB300]/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="text-xs tracking-[0.25em] uppercase text-[#A1A1AA] mb-3">Gesamt-Trinkgelder</div>
          <div className="text-6xl md:text-7xl font-black tracking-tight text-white" data-testid="staff-wallet-total">{wallet ? formatEUR(wallet.total) : "—"}</div>
          <div className="text-sm text-[#A1A1AA] mt-3">aus {wallet?.tip_count || 0} Trinkgeldern</div>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Heute" value={wallet?.today} icon={Calendar} accent="#EBB300" />
        <StatCard label="Diese Woche" value={wallet?.week} icon={TrendingUp} accent="#22C55E" />
        <StatCard label="Diesen Monat" value={wallet?.month} icon={Wallet} accent="#E05A33" />
      </div>

      {/* Locations */}
      {locations.length > 0 && (
        <div className="mb-8">
          <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-3">Deine Locations</div>
          <div className="flex flex-wrap gap-2">
            {locations.map((l) => (
              <div key={l.id} className="px-3 py-1.5 rounded-full bg-[#121216] border border-white/10 text-sm">{l.name}</div>
            ))}
          </div>
        </div>
      )}

      {/* Recent */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-[#EBB300]" />
          <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Letzte Trinkgelder</h2>
        </div>
        <div className="bg-[#121216] border border-white/5 rounded-2xl divide-y divide-white/5" data-testid="staff-recent-tips">
          {recent.length === 0 && <div className="p-6 text-center text-sm text-[#A1A1AA]">Noch keine Trinkgelder.</div>}
          {recent.slice(0, 12).map((t) => (
            <div key={t.id} className="flex items-center gap-4 p-4">
              <div className="text-xl font-black text-[#EBB300] w-24">{formatEUR(t.amount)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{t.customer_name || "Anonym"}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{t.location_name || "Direkt"} · {formatDateTime(t.created_at)}</div>
                {t.message && <div className="text-xs text-[#A1A1AA] truncate mt-1 italic">„{t.message}"</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-[#121216] border border-white/5 rounded-2xl p-5">
      <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        {label}
      </div>
      <div className="text-3xl font-black tracking-tight" style={{ color: accent }}>{value != null ? formatEUR(value) : "—"}</div>
    </div>
  );
}
